import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, Trash2, Edit2, FolderKanban, Calendar as CalendarIcon, Radio, ExternalLink, Eye, X, FileText, Upload, Map, Image as ImageIcon, Film } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = '/api';

const DEFAULT_IFRAMES = [
  { url: "https://whep.flue.live/?stream=JJrpa001", name: "JJrpa001" },
  { url: "https://whep.flue.live/?stream=JJrpa002", name: "JJrpa002" },
  { url: "https://whep.flue.live/?stream=JJrpa003", name: "JJrpa003" },
  { url: "https://perry.flue.live/?stream=JJrpa001&record", name: "JJrpa001 (Rec)" },
  { url: "https://whep.flue.live/?stream=JORGEX2001", name: "JORGEX2001" },
  { url: "https://whep.flue.live/?stream=JORGEX2002", name: "JORGEX2002" },
  { url: "https://whep.flue.live/?stream=JORGEX2003", name: "JORGEX2003" },
  { url: "https://perry.flue.live/?stream=JORGEX2001&record", name: "JORGEX2001 (Rec)" }
];

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contractExpiry, setContractExpiry] = useState(null);
  const [players, setPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerUrl, setNewPlayerUrl] = useState('');
  const [externalLinks, setExternalLinks] = useState([]);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [hasMap, setHasMap] = useState(false);
  const [hasVideos, setHasVideos] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.list();
      setProjects(response.data);
    } catch (error) { toast.error('Erro ao carregar projetos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProjects(); }, []);

  const resetForm = () => {
    setName(''); setDescription(''); setContractExpiry(null);
    setPlayers([]); setExternalLinks([]);
    setNewPlayerName(''); setNewPlayerUrl('');
    setNewLinkName(''); setNewLinkUrl('');
    setActiveTab('info'); setHasMap(false); setHasVideos(false);
    setLogoPreview(null);
  };

  const openCreateDialog = () => { resetForm(); setEditProject(null); setCreateOpen(true); };

  const openEditDialog = (project) => {
    setName(project.name);
    setDescription(project.description || '');
    setContractExpiry(project.contract_expiry ? new Date(project.contract_expiry) : null);
    setPlayers(project.players || []);
    setExternalLinks(project.external_links || []);
    setHasMap(project.has_map || false);
    setHasVideos(project.has_videos || false);
    setLogoPreview(project.logo_url || null);
    setEditProject(project);
    setActiveTab('info');
    setCreateOpen(true);
  };

  const generatePlayerId = () => `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const generateLinkId = () => `link_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addPlayer = () => {
    if (!newPlayerUrl) { toast.error('Informe a URL do player'); return; }
    setPlayers([...players, { id: generatePlayerId(), name: newPlayerName || `Player ${players.length + 1}`, url: newPlayerUrl }]);
    setNewPlayerName(''); setNewPlayerUrl('');
  };

  const addPlayerFromDefault = (defaultPlayer) => {
    if (players.some(p => p.url === defaultPlayer.url)) { toast.error('Player ja adicionado'); return; }
    setPlayers([...players, { id: generatePlayerId(), name: defaultPlayer.name, url: defaultPlayer.url }]);
  };

  const addExternalLink = () => {
    if (!newLinkUrl) { toast.error('Informe a URL do link'); return; }
    setExternalLinks([...externalLinks, { id: generateLinkId(), name: newLinkName || `Link ${externalLinks.length + 1}`, url: newLinkUrl }]);
    setNewLinkName(''); setNewLinkUrl('');
  };

  const handleUploadLogo = async (file, projectId) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Tipo nao permitido. Use JPG, PNG, WEBP ou GIF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande. Maximo 5MB'); return; }
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/projects/${projectId}/upload-logo`, formData, { headers });
      setLogoPreview(res.data.logo_url);
      if (editProject) setEditProject({ ...editProject, logo_url: res.data.logo_url });
      toast.success('Logo atualizada!');
      fetchProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar logo'); }
    finally { setUploadingLogo(false); }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath || !token) return null;
    return `${API_URL}/files/${logoPath}?auth=${token}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const projectData = {
      name, description,
      contract_expiry: contractExpiry?.toISOString() || null,
      players, external_links: externalLinks,
      iframes: players.map(p => p.url),
      has_map: hasMap,
      has_videos: hasVideos
    };
    try {
      if (editProject) {
        await projectsAPI.update(editProject.id, projectData);
        toast.success('Projeto atualizado!');
      } else {
        await projectsAPI.create(projectData);
        toast.success('Projeto criado!');
      }
      setCreateOpen(false); resetForm(); fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao salvar projeto'); }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) return;
    try { await projectsAPI.delete(projectId); toast.success('Projeto excluido!'); fetchProjects(); }
    catch (error) { toast.error('Erro ao excluir projeto'); }
  };

  const handleUploadDocument = async (file) => {
    if (!file || !editProject) return;
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/projects/${editProject.id}/upload-document`, formData, {
        headers
      });
      toast.success('Documento anexado!');
      const res = await projectsAPI.get(editProject.id);
      setEditProject(res.data);
      fetchProjects();
    } catch (e) { toast.error('Erro ao anexar documento'); }
    finally { setUploadingDoc(false); }
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const diff = new Date(date) - new Date();
    return diff < 30 * 24 * 60 * 60 * 1000 && diff > 0;
  };
  const isExpired = (date) => date && new Date(date) < new Date();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <FolderKanban size={20} className="text-gold" /> GESTAO DE PROJETOS
        </h2>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-project-btn"><Plus size={18} className="mr-2" />NOVO PROJETO</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border border-gray-200 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-gold font-rajdhani uppercase tracking-wider">{editProject ? 'EDITAR PROJETO' : 'CRIAR NOVO PROJETO'}</DialogTitle>
            </DialogHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className={`bg-gray-100 border border-gray-200 p-1 grid flex-shrink-0 grid-cols-4`}>
                <TabsTrigger value="info" className="data-[state=active]:bg-white data-[state=active]:text-gold text-sm"><FolderKanban size={14} className="mr-1" />INFO</TabsTrigger>
                <TabsTrigger value="players" className="data-[state=active]:bg-white data-[state=active]:text-gold text-sm"><Radio size={14} className="mr-1" />PLAYERS ({players.length})</TabsTrigger>
                <TabsTrigger value="links" className="data-[state=active]:bg-white data-[state=active]:text-gold text-sm"><ExternalLink size={14} className="mr-1" />LINKS ({externalLinks.length})</TabsTrigger>
                <TabsTrigger value="docs" className="data-[state=active]:bg-white data-[state=active]:text-gold text-sm"><FileText size={14} className="mr-1" />DOCS</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                <TabsContent value="info" className="flex-1 overflow-y-auto space-y-4 p-1 mt-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Nome do Projeto *</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="input-hud" placeholder="Ex: Operacao Seguranca Norte" required data-testid="project-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Descricao</Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input-hud min-h-[100px] resize-none" placeholder="Descricao do projeto..." />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Vencimento do Contrato</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal bg-gray-50 border-gray-200 hover:bg-gray-100">
                          <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                          {contractExpiry ? format(contractExpiry, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border border-gray-200" align="start">
                        <Calendar mode="single" selected={contractExpiry} onSelect={setContractExpiry} locale={ptBR} className="bg-white" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gold/5 rounded-lg border border-gold/20">
                    <input 
                      type="checkbox" 
                      id="has-map" 
                      checked={hasMap} 
                      onChange={(e) => setHasMap(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold accent-amber-600"
                      data-testid="has-map-checkbox"
                    />
                    <label htmlFor="has-map" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Map size={18} className="text-gold" />
                        <span className="text-navy text-sm font-semibold">Adicionar aba MAPA ao projeto</span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1 ml-6">Habilita a aba MAPA (KML) para Admin, Supervisor, Lideres e Vigilantes deste projeto</p>
                    </label>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gold/5 rounded-lg border border-gold/20">
                    <input 
                      type="checkbox" 
                      id="has-videos" 
                      checked={hasVideos} 
                      onChange={(e) => setHasVideos(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold accent-amber-600"
                      data-testid="has-videos-checkbox"
                    />
                    <label htmlFor="has-videos" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Film size={18} className="text-gold" />
                        <span className="text-navy text-sm font-semibold">Adicionar aba VIDEOS ao projeto</span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1 ml-6">Habilita upload e streaming de videos para todos os usuarios deste projeto</p>
                    </label>
                  </div>
                  {/* Logo Upload */}
                  {editProject && (
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="logo-upload-section">
                      <Label className="text-gray-500 font-mono text-xs uppercase flex items-center gap-1"><ImageIcon size={14} /> Logo do Projeto</Label>
                      {logoPreview && (
                        <div className="flex items-center gap-4">
                          <img src={getLogoUrl(logoPreview)} alt="Logo" className="w-24 h-24 object-contain rounded-lg border border-gray-200 bg-white p-1" data-testid="project-logo-preview" />
                          <span className="text-gray-400 text-xs font-mono">Logo atual</span>
                        </div>
                      )}
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors border border-dashed w-fit ${uploadingLogo ? 'border-gold/50 bg-gold/5 text-gold' : 'border-gray-300 text-gray-600 hover:border-gold hover:text-gold hover:bg-gold/5'}`}>
                        {uploadingLogo ? (
                          <><div className="w-3 h-3 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /> Enviando...</>
                        ) : (
                          <><Upload size={16} /> {logoPreview ? 'TROCAR LOGO' : 'ENVIAR LOGO'}</>
                        )}
                        <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => { handleUploadLogo(e.target.files[0], editProject.id); e.target.value = ''; }}
                          data-testid="upload-logo-input" />
                      </label>
                      <p className="text-gray-400 text-[10px] font-mono">JPG, PNG, WEBP ou GIF. Max 5MB</p>
                    </div>
                  )}
                  {!editProject && (
                    <p className="text-gray-400 text-xs font-mono p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <ImageIcon size={14} className="inline mr-1" /> Salve o projeto primeiro para enviar uma logo
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="players" className="flex-1 overflow-y-auto space-y-4 p-1 mt-4">
                  <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <Label className="text-gold font-mono text-xs uppercase">Adicionar Player Externo</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input value={newPlayerName} onChange={(e) => setNewPlayerName(e.target.value)} className="input-hud" placeholder="Nome do player" />
                      <div className="flex gap-2">
                        <Input value={newPlayerUrl} onChange={(e) => setNewPlayerUrl(e.target.value)} className="input-hud flex-1" placeholder="URL do iframe" />
                        <Button type="button" onClick={addPlayer} className="btn-primary px-4"><Plus size={16} /></Button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Players Disponiveis</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                      {DEFAULT_IFRAMES.map((iframe, i) => {
                        const isAdded = players.some(p => p.url === iframe.url);
                        return (
                          <button key={i} type="button" onClick={() => !isAdded && addPlayerFromDefault(iframe)} disabled={isAdded}
                            className={`flex items-center gap-2 p-2 text-left rounded-lg transition-colors text-sm ${isAdded ? 'bg-gold/10 border border-gold/30 text-gold cursor-not-allowed' : 'bg-white border border-gray-200 text-gray-600 hover:border-gold/30 hover:text-gold'}`}>
                            <Radio size={14} /><span className="font-mono truncate">{iframe.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {players.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-gray-500 font-mono text-xs uppercase">Players Adicionados ({players.length})</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {players.map((player) => (
                          <div key={player.id} className="flex items-center justify-between p-3 bg-gold/5 border border-gold/20 rounded-lg">
                            <div className="flex items-center gap-3 min-w-0">
                              <Radio size={16} className="text-gold flex-shrink-0" />
                              <div className="min-w-0"><p className="text-navy font-medium text-sm truncate">{player.name}</p><p className="text-gray-500 text-xs font-mono truncate">{player.url}</p></div>
                            </div>
                            <Button type="button" variant="ghost" size="icon" onClick={() => setPlayers(players.filter(p => p.id !== player.id))} className="text-gray-500 hover:text-red-500 flex-shrink-0"><Trash2 size={14} /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="links" className="flex-1 overflow-y-auto space-y-4 p-1 mt-4">
                  <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <Label className="text-gold font-mono text-xs uppercase">Adicionar Link Externo</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input value={newLinkName} onChange={(e) => setNewLinkName(e.target.value)} className="input-hud" placeholder="Nome do link" />
                      <div className="flex gap-2">
                        <Input value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)} className="input-hud flex-1" placeholder="URL completa (https://...)" />
                        <Button type="button" onClick={addExternalLink} className="btn-primary px-4"><Plus size={16} /></Button>
                      </div>
                    </div>
                  </div>
                  {externalLinks.length > 0 ? (
                    <div className="space-y-2">
                      {externalLinks.map((link) => (
                        <div key={link.id} className="flex items-center justify-between p-3 bg-blue-50/50 border border-blue-200/50 rounded-lg">
                          <div className="flex items-center gap-3 min-w-0">
                            <ExternalLink size={16} className="text-blue-600 flex-shrink-0" />
                            <div className="min-w-0"><p className="text-navy font-medium text-sm truncate">{link.name}</p><p className="text-gray-500 text-xs font-mono truncate">{link.url}</p></div>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => setExternalLinks(externalLinks.filter(l => l.id !== link.id))} className="text-gray-500 hover:text-red-500 flex-shrink-0"><Trash2 size={14} /></Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400"><ExternalLink size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">Nenhum link externo adicionado</p></div>
                  )}
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="docs" className="flex-1 overflow-y-auto space-y-4 p-1 mt-4">
                  {editProject ? (
                    <>
                      <div className="flex justify-end">
                        <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors border border-dashed ${uploadingDoc ? 'border-gold/50 bg-gold/5 text-gold' : 'border-gray-300 text-gray-600 hover:border-gold hover:text-gold hover:bg-gold/5'}`}>
                          {uploadingDoc ? (
                            <><div className="w-3 h-3 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /> Enviando...</>
                          ) : (
                            <><Upload size={16} /> ANEXAR DOCUMENTO</>
                          )}
                          <input type="file" className="hidden" onChange={(e) => { handleUploadDocument(e.target.files[0]); e.target.value = ''; }}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png" data-testid="upload-project-doc-input" />
                        </label>
                      </div>
                      {(editProject.documents || []).length > 0 ? (
                        <div className="space-y-2">
                          {editProject.documents.map((doc, i) => (
                            <div key={doc.id || i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-gold/50 transition-colors">
                              <a href={`${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 min-w-0 flex-1">
                                <FileText size={18} className="text-gold flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-navy font-medium text-sm truncate">{doc.filename}</p>
                                  <p className="text-gray-400 text-xs font-mono">{doc.uploaded_by} - {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</p>
                                </div>
                              </a>
                              <button onClick={async () => {
                                if (!window.confirm('Excluir este documento?')) return;
                                try {
                                  await axios.delete(`${API_URL}/projects/${editProject.id}/documents/${doc.id}`, { headers });
                                  toast.success('Documento excluido!');
                                  const res = await projectsAPI.get(editProject.id);
                                  setEditProject(res.data);
                                  fetchProjects();
                                } catch (e) { toast.error('Erro ao excluir'); }
                              }} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" data-testid={`delete-project-doc-${doc.id}`}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-400"><FileText size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">Nenhum documento anexado</p></div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <FileText size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Salve o projeto primeiro para anexar documentos</p>
                    </div>
                  )}
                </TabsContent>

                <div className="flex gap-3 pt-4 border-t border-gray-200 flex-shrink-0 mt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="btn-secondary flex-1">CANCELAR</Button>
                  <Button type="submit" className="btn-primary flex-1" data-testid="save-project-btn">{editProject ? 'ATUALIZAR' : 'CRIAR PROJETO'}</Button>
                </div>
              </form>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <div key={project.id} className="card-hud p-4 space-y-4" data-testid={`project-card-${project.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 min-w-0">
                {project.logo_url && (
                  <img src={getLogoUrl(project.logo_url)} alt="" className="w-10 h-10 object-contain rounded-md border border-gray-200 bg-white flex-shrink-0" data-testid={`project-logo-${project.id}`} />
                )}
                <div className="min-w-0">
                  <h3 className="text-navy font-rajdhani font-semibold text-lg uppercase truncate">{project.name}</h3>
                  {project.description && <p className="text-gray-500 text-sm mt-1 line-clamp-2">{project.description}</p>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${project.id}`)} className="text-green-600 hover:text-gold hover:bg-gold/10" data-testid={`view-project-${project.id}`}><Eye size={16} /></Button>
                <Button variant="ghost" size="icon" onClick={() => openEditDialog(project)} className="text-blue-600 hover:text-gold hover:bg-gold/10" data-testid={`edit-project-${project.id}`}><Edit2 size={16} /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(project.id)} className="text-gray-500 hover:text-red-500 hover:bg-red-50" data-testid={`delete-project-${project.id}`}><Trash2 size={16} /></Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center gap-2 text-sm"><Radio size={14} className="text-gold" /><span className="text-gray-500">{project.players?.length || 0} players</span></div>
              <div className="flex items-center gap-2 text-sm"><ExternalLink size={14} className="text-blue-600" /><span className="text-gray-500">{project.external_links?.length || 0} links</span></div>
              <div className="flex items-center gap-2 text-sm"><FileText size={14} className="text-green-600" /><span className="text-gray-500">{project.documents?.length || 0} docs</span></div>
            </div>
            {project.has_map && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-gold/10 rounded-md w-fit">
                <Map size={12} className="text-gold" />
                <span className="text-gold text-[11px] font-mono font-semibold">MAPA ATIVO</span>
              </div>
            )}
            {project.has_videos && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-gold/10 rounded-md w-fit">
                <Film size={12} className="text-gold" />
                <span className="text-gold text-[11px] font-mono font-semibold">VIDEOS ATIVO</span>
              </div>
            )}
            {project.contract_expiry && (
              <div className="flex items-center gap-2">
                <CalendarIcon size={14} className="text-gray-500" />
                <span className={`text-xs font-mono ${isExpired(project.contract_expiry) ? 'text-red-500' : isExpiringSoon(project.contract_expiry) ? 'text-gold' : 'text-gray-500'}`}>
                  Contrato: {new Date(project.contract_expiry).toLocaleDateString('pt-BR')}
                </span>
                {isExpired(project.contract_expiry) && <span className="badge-danger">EXPIRADO</span>}
                {isExpiringSoon(project.contract_expiry) && !isExpired(project.contract_expiry) && <span className="badge-warning">VENCENDO</span>}
              </div>
            )}
            <Button onClick={() => navigate(`/projects/${project.id}`)} className="w-full btn-secondary text-sm" data-testid={`enter-project-${project.id}`}>
              <Eye size={14} className="mr-2" /> ENTRAR NO PROJETO
            </Button>
          </div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="card-hud p-8 text-center">
          <FolderKanban size={48} className="text-gray-500 mx-auto mb-4" />
          <p className="text-gray-500 font-mono">Nenhum projeto cadastrado</p>
          <Button className="btn-primary mt-4" onClick={openCreateDialog}><Plus size={18} className="mr-2" />CRIAR PRIMEIRO PROJETO</Button>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
